interface Cell {
  row: number;
  col: number;
}

interface Cage {
  id: string;
  cells: Cell[];
  target: number;
  operation: string;
}

// Helper function to check if a number placement is safe (no row/col conflicts)
const isSafe = (board: number[][], size: number, row: number, col: number, num: number): boolean => {
  // Check row
  for (let x = 0; x < size; x++) {
    if (board[row][x] === num) {
      return false;
    }
  }

  // Check column
  for (let x = 0; x < size; x++) {
    if (board[x][col] === num) {
      return false;
    }
  }
  return true;
};

// Helper function to check if a cage constraint is satisfied
const checkCage = (board: number[][], cage: Cage): boolean => {
  const values = cage.cells.map(cell => board[cell.row][cell.col]);
  const isFull = !values.some(v => v === 0);

  if (isFull) {
    switch (cage.operation) {
      case '+':
        return values.reduce((a, b) => a + b, 0) === cage.target;
      case '*':
        return values.reduce((a, b) => a * b, 1) === cage.target;
      case '-':
        // For subtraction, order matters, so we check both possibilities
        return Math.abs(values[0] - values[1]) === cage.target;
      case '/':
        // For division, order matters, so we check both possibilities
        return Math.max(values[0], values[1]) / Math.min(values[0], values[1]) === cage.target;
      default:
        return false;
    }
  } else {
    // Partial check: if the current state makes it impossible to solve, prune the search
    switch (cage.operation) {
      case '+':
        return values.filter(v => v !== 0).reduce((a, b) => a + b, 0) < cage.target;
      case '*':
        const product = values.filter(v => v !== 0).reduce((a, b) => a * b, 1);
        return cage.target % product === 0; // Target must be divisible by current product
      case '-':
      case '/':
        // No easy way to prune for subtraction/division with partial values
        return true;
      default:
        return true;
    }
  }
};

// Helper function to find the next empty cell
const findEmpty = (board: number[][], size: number): [number, number] | null => {
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (board[r][c] === 0) {
        return [r, c];
      }
    }
  }
  return null;
};

// Main solver function
export const solveKenKen = (size: number, cages: Cage[], initialBoard?: number[][]): number[][] | null => {
  const board: number[][] = initialBoard ? initialBoard.map(row => [...row]) : Array.from({ length: size }, () => Array(size).fill(0));

  const solve = (): boolean => {
    const emptyCell = findEmpty(board, size);
    if (!emptyCell) {
      return true; // All cells are filled
    }

    const [row, col] = emptyCell;

    for (let num = 1; num <= size; num++) {
      if (isSafe(board, size, row, col, num)) {
        board[row][col] = num;

        const cage = cages.find(c => c.cells.some(cell => cell.row === row && cell.col === col));
        if (cage && !checkCage(board, cage)) {
          board[row][col] = 0; // Backtrack
          continue;
        }

        if (solve()) {
          return true;
        }
        board[row][col] = 0; // Backtrack
      }
    }
    return false;
  };

  if (solve()) {
    // Final check on all cages to be absolutely sure
    for (const cage of cages) {
      if (!checkCage(board, cage)) {
        return null;
      }
    }
    return board;
  } else {
    return null; // No solution found
  }
};

// Function to get a hint
export const getHint = (size: number, cages: Cage[], currentBoard: number[][]): { row: number; col: number; value: number } | null => {
  const solvedBoard = solveKenKen(size, cages, currentBoard);
  if (solvedBoard) {
    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size; c++) {
        if (currentBoard[r][c] === 0 && solvedBoard[r][c] !== 0) {
          return { row: r, col: c, value: solvedBoard[r][c] };
        }
      }
    }
  }
  return null;
};
